/**
 * Measure an uploaded export pair and propose a source profile from what the
 * files actually contain.
 *
 * This module exists because onboarding a source is otherwise a manual
 * measurement exercise: every shipped delta in `src/profiles/sources/` carries
 * hand-written FACT notes recording the numbers a human read out of real export
 * files (see `alabama-buys.json`, and `docs/forensic-bellingham-report.md` for
 * the long form). The scaffolder, `tools/new-profile.ts`, copies identity keys
 * from the base and admits in a note that they are unverified. This module does
 * the measuring instead.
 *
 * The governing constraint is AGENTS.md rule 1: never invent the data model.
 * Everything proposed here is measured from the bytes, and everything that
 * cannot be measured is WITHHELD and said to be withheld, rather than defaulted
 * to something plausible. Three consequences worth stating up front, because
 * each is a place where a helpful-seeming default would be a lie:
 *
 * - `safeBackfillFields` is always `[]`. Backfill approval is a human act
 *   (rules 4 and 6). No measurement can grant it, so this is a lock, not a
 *   default.
 * - `minimumMatchRate` is never proposed. Two files give one churn observation,
 *   not a floor. The observed rate goes in the evidence; the base value is
 *   inherited and flagged when the observation falls below it.
 * - `collectionPath` is never inferred. `inspectRecordsPath` in
 *   `source-loader.ts` refuses to guess a records path on the same grounds, so
 *   this module PROPOSES candidates and takes the confirmed path as an input.
 *
 * Pure and dependency-free beyond sibling engine modules, so the app, the
 * worker, and node tooling can all import it.
 */

import { isBlankStrict, isEmpty } from "./empty";
import { getCollection, normalizeIdentityValue } from "./normalize";
import type { SourceProfileBase, SourceProfileDelta } from "../profiles/schema";

/**
 * Fields whose values look like dates or timestamps are excluded from key
 * candidacy even when they measure as 100% unique.
 *
 * This is not a heuristic about field NAMES — that would be inventing a data
 * model. It is a guard against a real false positive: in
 * `src/test/fixtures/nashville-reference.json`, `PublishedDate` is non-blank and
 * distinct in all 40 records and so qualifies on the numbers alone, yet the
 * human who onboarded that source keyed on `ProjectCode`. Uniqueness in a small
 * sample is not evidence of identity, and a timestamp is the classic way to
 * accidentally satisfy it.
 */
const DATE_SHAPED = [
  /^\d{4}-\d{2}-\d{2}([T ]|$)/,
  /^\d{1,2}\/\d{1,2}\/\d{4}([T ]|$)/,
  /^\d{1,2}-\d{1,2}-\d{4}([T ]|$)/
];

/** Query parameters observed to change between runs of the same source. */
const VOLATILE_QUERY_PARAMS = ["tmstmp", "timestamp", "session", "sessionid", "sid", "token", "nonce", "_"];

/** Cap on pair-of-fields key candidates, so a 45-field export stays linear enough. */
const MAX_SINGLE_CANDIDATES_FOR_PAIRING = 12;

export type CollectionCandidate = {
  /** The value to use as `collectionPath`: a root key name, or "$" for a root array. */
  path: string;
  recordCount: number;
  /** Size of the union of record keys — a decent proxy for "is this the records array". */
  fieldCount: number;
};

export type FieldMeasurement = {
  field: string;
  /** Records where the value is not strict-blank, per `isBlankStrict`. */
  nonBlankCount: number;
  /** Records where the value is not empty, per `isEmpty` (placeholder-aware). */
  populatedCount: number;
  /** Distinct non-blank normalized values. */
  distinctCount: number;
  /** Distinct values shared by more than one record. */
  duplicateGroupCount: number;
  /** Value-type census of non-empty values, e.g. { string: 500 }. */
  typeCounts: Record<string, number>;
  /** Up to three observed values, for evidence. */
  samples: string[];
};

export type KeyCandidate = {
  fields: string[];
  /** True when the candidate is 100% non-blank and 100% distinct in BOTH files. */
  qualifies: boolean;
  /** Why it does not qualify, when it does not. */
  disqualifiedBecause: string[];
  /**
   * True when every record on both sides yields a key — nothing blank or
   * missing. Weaker than `qualifies`, which also demands full distinctness.
   * A complete-but-not-distinct candidate is still measurable across the pair,
   * and on a re-issuing source it is the RIGHT key (see `perRunValue`).
   */
  complete: boolean;
  baseline: { nonBlankCount: number; distinctCount: number; duplicateGroups: number };
  latest: { nonBlankCount: number; distinctCount: number; duplicateGroups: number };
  /** Records sharing a key across the pair; null when the candidate is not complete. */
  sharedCount: number | null;
  /** sharedCount / baseline record count; null when the candidate is not complete. */
  matchRate: number | null;
  /**
   * Perfectly distinct on both sides yet sharing NOTHING across them.
   *
   * That combination is the signature of a value minted per run — a session
   * token in a URL, a run-scoped id — not of an identity. Measured, not
   * guessed: the three SciQuest sources each have a `BidURL` that is unique in
   * both runs and matches 0 records across them, while their `ProjectCode`
   * matches 100% despite carrying duplicates. Uniqueness is a within-file
   * property; identity has to survive between files too.
   */
  perRunValue: boolean;
};

export type SourceMeasurement = {
  collectionPath: string;
  baselineRecordCount: number;
  latestRecordCount: number;
  /** Union of record keys across both files, sorted. */
  fields: string[];
  baselineFields: Record<string, FieldMeasurement>;
  latestFields: Record<string, FieldMeasurement>;
  /** Fields holding exactly one distinct value across every record in both files. */
  constantFields: Array<{ field: string; value: string }>;
  /** Constant fields whose value parses as an http(s) URL. */
  constantUrlFields: Array<{ field: string; value: string; origin: string; strippedParams: string[] }>;
  /** Fields non-blank in 100% of records in both files. */
  alwaysPopulatedFields: string[];
  /** Fields blank in 100% of records in both files. */
  neverPopulatedFields: string[];
  keyCandidates: KeyCandidate[];
  /** Qualifying candidates that produce the identical pairing across the pair. */
  agreeingCandidates: string[][];
  /** Groups of >= 2 records sharing a title, per field, in the baseline. */
  duplicateTitleGroups: number;
};

/**
 * Root keys whose value is an array of objects, plus the root itself when it is
 * one. Proposed for confirmation, never adopted: see the module header.
 */
export function proposeCollectionPaths(dataset: unknown): CollectionCandidate[] {
  const candidates: CollectionCandidate[] = [];

  const describe = (path: string): CollectionCandidate | null => {
    const records = getCollection(dataset, path);
    if (records.length === 0) return null;
    const fields = new Set<string>();
    for (const record of records) {
      for (const field of Object.keys(record)) fields.add(field);
    }
    return { path, recordCount: records.length, fieldCount: fields.size };
  };

  if (Array.isArray(dataset)) {
    const root = describe("$");
    if (root) candidates.push(root);
    return candidates;
  }

  if (dataset === null || typeof dataset !== "object") return candidates;

  for (const key of Object.keys(dataset as Record<string, unknown>)) {
    const candidate = describe(key);
    if (candidate) candidates.push(candidate);
  }
  // Most records first: the records array is virtually always the largest.
  return candidates.sort((left, right) => right.recordCount - left.recordCount);
}

/** Root keys actually present, for the "nothing looks like a records array" message. */
export function describeRoot(dataset: unknown): string {
  if (Array.isArray(dataset)) return "(root is an array)";
  if (dataset === null || typeof dataset !== "object") return "(root is not an object)";
  const keys = Object.keys(dataset as Record<string, unknown>);
  return keys.length > 0 ? keys.join(", ") : "(none)";
}

function measureFields(records: Array<Record<string, unknown>>, fields: string[]): Record<string, FieldMeasurement> {
  const out: Record<string, FieldMeasurement> = {};
  for (const field of fields) {
    const valueCounts = new Map<string, number>();
    const typeCounts: Record<string, number> = {};
    let nonBlankCount = 0;
    let populatedCount = 0;

    for (const record of records) {
      const value = record[field];
      if (!isBlankStrict(value)) {
        nonBlankCount++;
        const serialized = typeof value === "string" ? value.trim() : JSON.stringify(value) ?? "";
        valueCounts.set(serialized, (valueCounts.get(serialized) ?? 0) + 1);
      }
      if (!isEmpty(value)) {
        populatedCount++;
        const type = Array.isArray(value) ? "array" : typeof value;
        typeCounts[type] = (typeCounts[type] ?? 0) + 1;
      }
    }

    let duplicateGroupCount = 0;
    for (const count of valueCounts.values()) {
      if (count > 1) duplicateGroupCount++;
    }

    out[field] = {
      field,
      nonBlankCount,
      populatedCount,
      distinctCount: valueCounts.size,
      duplicateGroupCount,
      typeCounts,
      samples: [...valueCounts.keys()].slice(0, 3)
    };
  }
  return out;
}

function looksDateShaped(samples: string[]): boolean {
  if (samples.length === 0) return false;
  return samples.every((sample) => DATE_SHAPED.some((pattern) => pattern.test(sample)));
}

/** Strip volatile query parameters, returning the cleaned URL and what was removed. */
export function stripVolatileParams(raw: string): { cleaned: string; stripped: string[] } {
  if (!URL.canParse(raw)) return { cleaned: raw, stripped: [] };
  const url = new URL(raw);
  const stripped: string[] = [];
  for (const name of [...url.searchParams.keys()]) {
    if (VOLATILE_QUERY_PARAMS.includes(name.toLowerCase())) {
      stripped.push(name);
      url.searchParams.delete(name);
    }
  }
  return { cleaned: url.toString(), stripped };
}

/**
 * Normalized identity values for one field, positionally aligned with the
 * records, `null` where the record cannot be keyed on that field.
 *
 * Every key candidate is built from these columns rather than from the records,
 * because normalization is the expensive part: `normalizeIdentityValue` runs
 * NFC and, for anything scheme-shaped, the URL parser. Measuring 45 singles and
 * their pairs straight off the records re-normalized the same 1,000 values once
 * per candidate per pass — which is what made the panel take two seconds to
 * open. Normalizing once per field and reusing the columns costs 45 passes
 * total, whatever the candidate count.
 */
type IdentityColumn = Array<string | null>;

function identityColumns(
  records: Array<Record<string, unknown>>,
  fields: string[]
): Record<string, IdentityColumn> {
  const columns: Record<string, IdentityColumn> = {};
  for (const field of fields) {
    const column: IdentityColumn = new Array(records.length);
    for (let index = 0; index < records.length; index++) {
      column[index] = normalizeIdentityValue(records[index]![field]);
    }
    columns[field] = column;
  }
  return columns;
}

type KeyTally = {
  /** Records yielding a key — every component non-blank, present, and scalar. */
  keyedCount: number;
  /** Distinct keys. */
  distinctCount: number;
  /** Distinct keys carried by more than one record. */
  duplicateGroups: number;
  /** The key values themselves, for the cross-run intersection. */
  keys: Set<string>;
};

/**
 * Count one candidate over one side, in a single pass.
 *
 * Components are serialized with JSON.stringify, as `buildIdentityKey` does and
 * for the same reason: a value containing the separator must not be able to
 * forge a collision. A single-field candidate needs no wrapper — its normalized
 * value is already an injective key — and keys are only ever compared with
 * other keys built from the same fields.
 */
function tallyKeys(fields: string[], columns: Record<string, IdentityColumn>, recordCount: number): KeyTally {
  const counts = new Map<string, number>();
  let keyedCount = 0;

  if (fields.length === 1) {
    const column = columns[fields[0]!] ?? [];
    for (let index = 0; index < recordCount; index++) {
      const value = column[index] ?? null;
      if (value === null) continue;
      keyedCount++;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  } else if (fields.length > 1) {
    const columnsInOrder = fields.map((field) => columns[field] ?? []);
    const parts: string[] = new Array(columnsInOrder.length);
    for (let index = 0; index < recordCount; index++) {
      let complete = true;
      for (let position = 0; position < columnsInOrder.length; position++) {
        const value = columnsInOrder[position]![index] ?? null;
        if (value === null) {
          complete = false;
          break;
        }
        parts[position] = value;
      }
      if (!complete) continue;
      keyedCount++;
      const key = JSON.stringify(parts);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  let duplicateGroups = 0;
  for (const count of counts.values()) if (count > 1) duplicateGroups++;
  return { keyedCount, distinctCount: counts.size, duplicateGroups, keys: new Set(counts.keys()) };
}

/** Records keyed identically on both sides. */
function countShared(baseline: Set<string>, latest: Set<string>): number {
  // Walk the smaller set: the intersection is the same either way.
  const [smaller, larger] = baseline.size <= latest.size ? [baseline, latest] : [latest, baseline];
  let shared = 0;
  for (const key of smaller) {
    if (larger.has(key)) shared++;
  }
  return shared;
}

type MeasurementContext = {
  baselineColumns: Record<string, IdentityColumn>;
  latestColumns: Record<string, IdentityColumn>;
  baselineCount: number;
  latestCount: number;
  baselineFields: Record<string, FieldMeasurement>;
};

function measureCandidate(fields: string[], context: MeasurementContext): KeyCandidate {
  const { baselineCount, latestCount, baselineFields } = context;
  const disqualifiedBecause: string[] = [];

  const inBaseline = tallyKeys(fields, context.baselineColumns, baselineCount);
  const inLatest = tallyKeys(fields, context.latestColumns, latestCount);

  if (inBaseline.keyedCount < baselineCount || inLatest.keyedCount < latestCount) {
    disqualifiedBecause.push("blank in at least one record");
  }
  if (inBaseline.distinctCount < baselineCount || inLatest.distinctCount < latestCount) {
    disqualifiedBecause.push("not distinct in every record");
  }
  for (const field of fields) {
    if (looksDateShaped(baselineFields[field]?.samples ?? [])) {
      disqualifiedBecause.push(`"${field}" holds date-shaped values; a timestamp is not an identity`);
    }
  }

  const qualifies = disqualifiedBecause.length === 0;
  // Completeness, not distinctness, is what makes a candidate measurable across
  // the pair. Restricting the cross-run measurement to fully-distinct keys hid
  // the answer on every re-issuing source: the key a human picks there has
  // duplicates in one run and still matches everything in the other.
  const complete =
    baselineCount > 0 &&
    latestCount > 0 &&
    inBaseline.keyedCount === baselineCount &&
    inLatest.keyedCount === latestCount;

  let sharedCount: number | null = null;
  let matchRate: number | null = null;
  if (complete) {
    sharedCount = countShared(inBaseline.keys, inLatest.keys);
    matchRate = baselineCount > 0 ? sharedCount / baselineCount : 0;
  }

  const fullyDistinct = inBaseline.distinctCount === baselineCount && inLatest.distinctCount === latestCount;
  const perRunValue = complete && fullyDistinct && sharedCount === 0;
  if (perRunValue) {
    disqualifiedBecause.push(
      "unique in both runs but shares no record across them — a value minted per run, not an identity"
    );
  }

  return {
    fields,
    qualifies: qualifies && !perRunValue,
    disqualifiedBecause,
    complete,
    baseline: {
      nonBlankCount: inBaseline.keyedCount,
      distinctCount: inBaseline.distinctCount,
      duplicateGroups: inBaseline.duplicateGroups
    },
    latest: {
      nonBlankCount: inLatest.keyedCount,
      distinctCount: inLatest.distinctCount,
      duplicateGroups: inLatest.duplicateGroups
    },
    sharedCount,
    matchRate,
    perRunValue
  };
}

/**
 * Measure the pair. `collectionPath` is an input, confirmed by the caller —
 * this function never decides where the records live.
 */
export function measureSource(input: {
  baseline: unknown;
  latest: unknown;
  collectionPath: string;
  /** Field to count duplicate-title groups on; from the base's alert config. */
  titleField?: string;
}): SourceMeasurement {
  const { collectionPath, titleField = "Title" } = input;
  const baseline = getCollection(input.baseline, collectionPath);
  const latest = getCollection(input.latest, collectionPath);

  const fieldSet = new Set<string>();
  for (const record of [...baseline, ...latest]) {
    for (const field of Object.keys(record)) fieldSet.add(field);
  }
  const fields = [...fieldSet].sort();

  const baselineFields = measureFields(baseline, fields);
  const latestFields = measureFields(latest, fields);

  const constantFields: SourceMeasurement["constantFields"] = [];
  const constantUrlFields: SourceMeasurement["constantUrlFields"] = [];
  const alwaysPopulatedFields: string[] = [];
  const neverPopulatedFields: string[] = [];

  for (const field of fields) {
    const inBaseline = baselineFields[field]!;
    const inLatest = latestFields[field]!;

    const fullyPopulated =
      baseline.length > 0 &&
      latest.length > 0 &&
      inBaseline.nonBlankCount === baseline.length &&
      inLatest.nonBlankCount === latest.length;
    if (fullyPopulated) alwaysPopulatedFields.push(field);
    if (inBaseline.nonBlankCount === 0 && inLatest.nonBlankCount === 0) neverPopulatedFields.push(field);

    // Constant means one distinct value across BOTH files, present everywhere.
    if (fullyPopulated && inBaseline.distinctCount === 1 && inLatest.distinctCount === 1) {
      const value = inBaseline.samples[0] ?? "";
      if (value === (inLatest.samples[0] ?? "")) {
        constantFields.push({ field, value });
        if (URL.canParse(value)) {
          const { cleaned, stripped } = stripVolatileParams(value);
          constantUrlFields.push({ field, value, origin: new URL(cleaned).origin, strippedParams: stripped });
        }
      }
    }
  }

  // Single-field candidates first, then pairs of the most promising singles.
  // A constant field is never a key on its own but is the identity prefix every
  // shipped profile uses, so it is paired with each discriminating field.
  const constantNames = new Set(constantFields.map((entry) => entry.field));
  const discriminating = fields.filter((field) => !constantNames.has(field));

  // Normalize each field once; every candidate below reads these columns.
  const context: MeasurementContext = {
    baselineColumns: identityColumns(baseline, fields),
    latestColumns: identityColumns(latest, fields),
    baselineCount: baseline.length,
    latestCount: latest.length,
    baselineFields
  };

  const candidates: KeyCandidate[] = [];
  const singles = new Map<string, KeyCandidate>();
  for (const field of fields) {
    const candidate = measureCandidate([field], context);
    singles.set(field, candidate);
    candidates.push(candidate);
  }

  // Pair the identity constants with every field that could carry identity, not
  // only the ones already unique on their own.
  //
  // The old rule — pair only fully-qualifying singles — is what made a
  // re-issuing source unsolvable. UNM's ProjectCode is 677/677 distinct in one
  // run and re-issued 29 times in the next, so it failed, so AgentID+ProjectCode
  // was never even measured, and the generator reported "no key" about a source
  // whose key a human had already chosen. Completeness is the right gate;
  // ranking by cross-run match rate below sorts out the rest.
  const promising = discriminating
    .filter((field) => singles.get(field)?.complete)
    .sort(
      (a, b) => (singles.get(b)?.baseline.distinctCount ?? 0) - (singles.get(a)?.baseline.distinctCount ?? 0)
    )
    .slice(0, MAX_SINGLE_CANDIDATES_FOR_PAIRING);

  for (const constant of constantFields) {
    for (const field of promising) {
      candidates.push(measureCandidate([constant.field, field], context));
    }
  }

  // Which qualifying candidates produce the identical pairing? Agreement across
  // two independently-derived keys is the strongest evidence a key is real —
  // the Bellingham forensic found BidURL and ProjectCode agreeing on 499/499.
  const usable = candidates.filter((candidate) => candidate.complete && !candidate.perRunValue);
  const agreeingCandidates: string[][] = [];
  for (const candidate of usable) {
    if (candidate.sharedCount !== null && candidate.sharedCount === usable[0]?.sharedCount) {
      agreeingCandidates.push(candidate.fields);
    }
  }

  let duplicateTitleGroups = 0;
  if (fields.includes(titleField)) {
    const counts = new Map<string, number>();
    for (const record of baseline) {
      const value = record[titleField];
      if (typeof value !== "string" || value.trim().length === 0) continue;
      counts.set(value.trim(), (counts.get(value.trim()) ?? 0) + 1);
    }
    for (const count of counts.values()) {
      if (count >= 2) duplicateTitleGroups++;
    }
  }

  return {
    collectionPath,
    baselineRecordCount: baseline.length,
    latestRecordCount: latest.length,
    fields,
    baselineFields,
    latestFields,
    constantFields,
    constantUrlFields,
    alwaysPopulatedFields,
    neverPopulatedFields,
    keyCandidates: candidates,
    agreeingCandidates,
    duplicateTitleGroups
  };
}

/**
 * Does a candidate tell records apart?
 *
 * Fully distinct always counts — including the degenerate single-record export,
 * where one distinct value over one record is all the distinctness available.
 * Otherwise it has to hold more than one value: a candidate carrying one value
 * across many records identifies nothing, which is definitional rather than a
 * tuned threshold.
 */
function discriminates(distinctCount: number, recordCount: number): boolean {
  return distinctCount === recordCount || distinctCount > 1;
}

/** The fleet's detection convention, enforced by all-profiles.invariants.test.ts. */
const FLEET_IDENTITY_FIELDS = ["AgentID", "AgentName"];

export type ProfileProposal = {
  delta: SourceProfileDelta;
  /** Evidence notes, in the format the hand-written deltas use. */
  notes: string[];
  /**
   * Non-empty when the measurement cannot support a profile. A caller MUST
   * refuse to save a draft whose `blocked` is non-empty; the delta alongside is
   * for display only.
   */
  blocked: string[];
  /** Non-blocking cautions: things a human should look at before committing. */
  warnings: string[];
};

/**
 * Turn a measurement into a proposed delta.
 *
 * Takes the base because several outputs are NARROWINGS of base lists rather
 * than free constructions. That asymmetry is the rule that makes them
 * legitimate: measurement may subtract from an inherited requirement, never add
 * to one. Asserting a field is required when the source never populates it is
 * contradicted by the data, so removing it is a measurement; asserting a field
 * SHOULD be required is a policy claim no measurement supports.
 */
export function proposeDelta(
  measurement: SourceMeasurement,
  base: SourceProfileBase,
  identity: { id: string; sourceUrl?: string; displayName?: string; agency?: string }
): ProfileProposal {
  const blocked: string[] = [];
  const warnings: string[] = [];
  const notes: string[] = [];

  if (measurement.baselineRecordCount === 0 || measurement.latestRecordCount === 0) {
    blocked.push(
      `No records found at "${measurement.collectionPath}" in ${measurement.baselineRecordCount === 0 ? "the baseline" : "the latest"} file.`
    );
  }

  // --- Identity and detection -------------------------------------------
  const constantByField = new Map(measurement.constantFields.map((entry) => [entry.field, entry.value]));
  const identityValues: Record<string, string[]> = {};
  for (const field of FLEET_IDENTITY_FIELDS) {
    const value = constantByField.get(field);
    if (value !== undefined) identityValues[field] = [value];
  }
  const missingIdentity = FLEET_IDENTITY_FIELDS.filter((field) => !(field in identityValues));
  if (missingIdentity.length > 0) {
    warnings.push(
      `The fleet invariant requires detection.identityValues to name both AgentID and AgentName; ` +
        `${missingIdentity.join(" and ")} ${missingIdentity.length === 1 ? "is" : "are"} not constant in these files. ` +
        `This draft will fail src/profiles/all-profiles.invariants.test.ts if committed as-is.`
    );
  }
  const otherConstants = measurement.constantFields
    .filter((entry) => !FLEET_IDENTITY_FIELDS.includes(entry.field))
    .map((entry) => entry.field);

  // sourceUrl is the ORIGIN of a constant URL field, not the full constant.
  // Both shipped examples confirm this: Bellingham's constant ResourceURL is
  // https://cob.procureware.com/Bids while its sourceUrl is the bare origin.
  const urlConstant = measurement.constantUrlFields[0];
  const sourceUrl = identity.sourceUrl ?? urlConstant?.origin;
  if (sourceUrl === undefined) {
    blocked.push(
      "No constant URL-shaped field was found, so sourceUrl cannot be measured. Supply one explicitly."
    );
  }

  // --- Keys --------------------------------------------------------------
  /**
   * Candidates ranked by how well they identify the SAME record across the two
   * runs, which is the job a key actually has.
   *
   * Cross-run match rate leads, ahead of within-file uniqueness, because the two
   * can point opposite ways: on all three SciQuest sources `BidURL` is unique in
   * both runs and matches 0 records across them (a per-run token, now excluded
   * outright), while `ProjectCode` matches every record despite carrying
   * duplicates. Fully-distinct keys still win ties, so a source with a clean key
   * is unaffected.
   */
  const constantNames = new Set(measurement.constantFields.map((entry) => entry.field));
  const qualifying = measurement.keyCandidates
    .filter((candidate) => candidate.complete && !candidate.perRunValue)
    // A key has to tell records apart within a run before it can match them
    // across two. A candidate made only of constants, or holding one distinct
    // value, identifies nothing — that is definitional, not a threshold, and it
    // is why widening the completeness gate needs this guard: constants are
    // complete by definition and would otherwise rank on match rate alone.
    .filter(
      (candidate) =>
        !candidate.fields.every((field) => constantNames.has(field)) &&
        discriminates(candidate.baseline.distinctCount, measurement.baselineRecordCount) &&
        discriminates(candidate.latest.distinctCount, measurement.latestRecordCount)
    )
    .sort((left, right) => {
      const byRate = (right.matchRate ?? 0) - (left.matchRate ?? 0);
      if (byRate !== 0) return byRate;
      // A key with no duplicates beats an equally-matching one that has some.
      const byDuplicates =
        left.baseline.duplicateGroups + left.latest.duplicateGroups -
        (right.baseline.duplicateGroups + right.latest.duplicateGroups);
      if (byDuplicates !== 0) return byDuplicates;
      // Prefer a composite that carries the identity prefix, as every shipped
      // profile does, then prefer the shorter key.
      const leftHasIdentity = left.fields.some((field) => field in identityValues) ? 1 : 0;
      const rightHasIdentity = right.fields.some((field) => field in identityValues) ? 1 : 0;
      if (leftHasIdentity !== rightHasIdentity) return rightHasIdentity - leftHasIdentity;
      return left.fields.length - right.fields.length;
    });

  // Every shipped profile prefixes its key with the bot identity, and with the
  // FIRST identity field specifically (AgentID), not with any constant that
  // happens to discriminate. Pairing every constant would propose
  // ["LegacyAgentID","BidURL"] and ["ResourceURL","BidURL"] as fallbacks, which
  // measure identically but are not what a human picks. The measurement layer
  // reports all of them as evidence; the fleet convention is applied here.
  const identityPrefix = FLEET_IDENTITY_FIELDS.find((field) => field in identityValues);
  const composite = qualifying.filter(
    (candidate) => candidate.fields.length > 1 && candidate.fields[0] === identityPrefix
  );
  const primary = composite[0] ?? qualifying[0];
  if (!primary && (measurement.baselineRecordCount === 1 || measurement.latestRecordCount === 1)) {
    blocked.push(
      "A single-record export cannot establish a key: with one row every field holds exactly one value, so nothing " +
        "distinguishes records. Measure a run with several records."
    );
  } else if (!primary) {
    blocked.push(
      "No key candidate qualified: no field or field pair is non-blank in every record of both files while also " +
        "identifying records across them. " +
        "A profile with a key nobody verified is exactly what this refuses to produce."
    );
  }

  /**
   * A fallback has to be an identity in its own right that produces the SAME
   * pairing as the primary — the Bellingham forensic's "two keys independently
   * produce the identical pairing" evidence.
   *
   * Both halves matter. Without the strictness test, widening candidate
   * generation swept in document arrays and timestamps that happen to be
   * complete; without the same-pairing test, a key that matches a different
   * subset would be offered as an equivalent, which it is not.
   */
  const fallbacks = composite
    .filter(
      (candidate) =>
        candidate !== primary &&
        candidate.qualifies &&
        candidate.sharedCount !== null &&
        candidate.sharedCount === primary?.sharedCount
    )
    .map((candidate) => candidate.fields);

  /**
   * Dedupe and matching are different questions, so they can want different keys.
   *
   * Dedupe asks "are these two rows in THIS export the same record?" — a
   * within-run question. Matching asks "is this row the same record as one in
   * the OTHER run?" — a cross-run question. A per-run token answers the first
   * perfectly and the second not at all, which is exactly why the three
   * SciQuest profiles match on `ProjectCode` and dedupe on `BidURL`.
   *
   * So: dedupe on the primary key when it is unique within both runs, and
   * otherwise on the best key that is. That reproduces every committed profile —
   * Bellingham and Nashville dedupe on their primary because it is unique;
   * the re-issuing sources fall through to their per-run-unique URL.
   */
  const uniqueWithinRuns = (candidate: KeyCandidate) =>
    candidate.complete &&
    candidate.baseline.duplicateGroups === 0 &&
    candidate.latest.duplicateGroups === 0 &&
    candidate.baseline.distinctCount > 1;
  const dedupeFallback = measurement.keyCandidates
    .filter((candidate) => candidate.fields[0] === identityPrefix && candidate.fields.length > 1)
    .filter(uniqueWithinRuns)[0];
  const dedupeKeyFields =
    primary && uniqueWithinRuns(primary)
      ? primary.fields
      : (dedupeFallback?.fields ?? primary?.fields ?? []);
  if (primary && dedupeKeyFields !== primary.fields && dedupeFallback) {
    notes.push(
      `FACT — DEDUPE KEY DIFFERS FROM MATCH KEY: matching uses ${JSON.stringify(primary.fields)}, which identifies ` +
        `records across the pair but repeats within a run; dedupe uses ${JSON.stringify(dedupeFallback.fields)}, ` +
        `which is unique inside each run but shares nothing across them. Dedupe is a within-run question and ` +
        `matching is a cross-run one, so the two keys are allowed to differ.`
    );
  }

  // --- Requirement narrowing (subtract only) ------------------------------
  const never = new Set(measurement.neverPopulatedFields);
  const hardRequiredFields = base.hardRequiredFields.filter((field) => !never.has(field));
  const requiredFields = base.quality.requiredFields.filter((field) => !never.has(field));
  const droppedRequired = [
    ...base.hardRequiredFields.filter((field) => never.has(field)),
    ...base.quality.requiredFields.filter((field) => never.has(field))
  ];

  // --- Notes: the evidence, in the format the hand-written deltas use ------
  notes.push(
    "STATUS: UNAPPROVED at v1. No field is approved for automatic backfill; per AGENTS.md rule 4 a field becomes " +
      "backfillable only when a per-source decision adds it to safeBackfillFields (rule 6 fields additionally need an " +
      "explicit approval note), accompanied by a version bump. Everything below was MEASURED from the two files named " +
      "here by src/engine/profile-inference.ts, not assumed."
  );

  if (Object.keys(identityValues).length > 0) {
    const stated = Object.entries(identityValues)
      .map(([field, values]) => `${field} ${JSON.stringify(values[0])}`)
      .join(" and ");
    notes.push(
      `IDENTITY: ${stated} constant across ${measurement.baselineRecordCount}/${measurement.baselineRecordCount} and ` +
        `${measurement.latestRecordCount}/${measurement.latestRecordCount} records. Detection ANDs both; AgentID alone is ` +
        `not unique across the fleet — 1234 is carried by two bots.` +
        (otherConstants.length > 0
          ? ` Also constant but NOT used for detection: ${otherConstants.join(", ")}.`
          : "")
    );
  }

  notes.push(
    `FACT — SHAPE: records at "${measurement.collectionPath}"; ${measurement.baselineRecordCount} and ` +
      `${measurement.latestRecordCount} records; ${measurement.fields.length} distinct fields across the pair; ` +
      `${measurement.alwaysPopulatedFields.length} populated in 100% of records, ` +
      `${measurement.neverPopulatedFields.length} empty in 100%.`
  );

  // Only the fields that bear on the key decision, as the hand-written notes do
  // — every populated field would bury the evidence in a 45-line census.
  const keyRelevant = new Set<string>([
    ...(primary?.fields ?? []),
    ...fallbacks.flat(),
    ...measurement.constantFields.map((entry) => entry.field)
  ]);
  const keyLines = measurement.keyCandidates
    .filter((candidate) => candidate.fields.length === 1)
    .filter(
      (candidate) =>
        keyRelevant.has(candidate.fields[0]!) ||
        candidate.baseline.distinctCount === measurement.baselineRecordCount
    )
    .map((candidate) => {
      const constant = candidate.baseline.distinctCount === 1 && candidate.baseline.nonBlankCount > 0;
      const suffix = constant
        ? " (constant — a tenant discriminator, never a key on its own)"
        : candidate.qualifies
          ? ""
          : ` — NOT a key candidate: ${candidate.disqualifiedBecause.join("; ")}`;
      return (
        `${candidate.fields[0]} non-blank ${candidate.baseline.nonBlankCount}/${measurement.baselineRecordCount}, ` +
        `distinct ${candidate.baseline.distinctCount}${suffix}`
      );
    });
  if (keyLines.length > 0) {
    notes.push(`FACT — KEYS (baseline, ${measurement.baselineRecordCount} records): ${keyLines.join("; ")}.`);
  }

  if (primary) {
    const rate = primary.matchRate ?? 0;
    const belowFloor = rate < base.minimumMatchRate;
    notes.push(
      `FACT — PAIR: baseline (${measurement.baselineRecordCount}) vs latest (${measurement.latestRecordCount}) on ` +
        `${JSON.stringify(primary.fields)}: ${primary.sharedCount} shared, match rate ${rate.toFixed(3)}. ` +
        `minimumMatchRate is INHERITED from the base (${base.minimumMatchRate}) and NOT measured — one pair is a single ` +
        `churn observation, not a floor.` +
        (belowFloor
          ? ` This observation is BELOW the inherited floor; ASSUMPTION — requires an approved per-source floor before a ` +
            `failure here is treated as a regression.`
          : "")
    );
  }

  // A key that carries duplicates still identifies records across runs on a
  // re-issuing source, but it is a judgment the measurement cannot make alone —
  // so it is stated as an assumption, the way the hand-written deltas do.
  const primaryDuplicates = primary
    ? primary.baseline.duplicateGroups + primary.latest.duplicateGroups
    : 0;
  if (primary && primaryDuplicates > 0) {
    warnings.push(
      `The proposed key ${JSON.stringify(primary.fields)} is not unique: ` +
        `${primary.baseline.duplicateGroups} duplicate group(s) in the baseline and ` +
        `${primary.latest.duplicateGroups} in the latest. It still matches ${primary.sharedCount} record(s) across ` +
        `the pair, which is why it was chosen, but a re-issuing source needs an owner decision on whether those ` +
        `duplicates are the same solicitation or different ones.`
    );
    notes.push(
      `ASSUMPTION — NON-UNIQUE KEY: ${JSON.stringify(primary.fields)} carries ` +
        `${primary.baseline.duplicateGroups} duplicate group(s) in the baseline and ` +
        `${primary.latest.duplicateGroups} in the latest, yet matches ${primary.sharedCount}/` +
        `${measurement.baselineRecordCount} records across the pair. This source appears to re-issue events under ` +
        `one code. The key is proposed on cross-run evidence, NOT on uniqueness; whether duplicate rows are the same ` +
        `solicitation is a per-source decision the measurement cannot make (AGENTS.md rule 1).`
    );
  }

  const perRunValues = measurement.keyCandidates.filter((candidate) => candidate.perRunValue);
  if (perRunValues.length > 0) {
    notes.push(
      `FACT — PER-RUN VALUES: ${perRunValues.map((c) => JSON.stringify(c.fields)).join(", ")} ` +
        `${perRunValues.length === 1 ? "is" : "are"} unique in BOTH runs yet share ZERO records across them. That is ` +
        `a value minted per run (a session token or run-scoped id), not an identity, so it is excluded from key ` +
        `candidacy however unique it looks within one file.`
    );
  }

  if (primary && fallbacks.length > 0) {
    const agreeing = [primary.fields, ...fallbacks].map((fields) => JSON.stringify(fields));
    notes.push(
      `FACT — AGREEMENT: ${agreeing.join(" and ")} produce the identical pairing across the pair ` +
        `(${primary.sharedCount} shared each). Two independently-derived keys agreeing is the strongest available ` +
        `evidence that the pairing is real.`
    );
  }

  if (measurement.neverPopulatedFields.length > 0) {
    notes.push(
      `FACT — FILL: empty in 100% of records in both files: ${measurement.neverPopulatedFields.join(", ")}. ` +
        `Deliberately NOT excluded — a field that starts carrying data is information, not noise.`
    );
  }

  if (droppedRequired.length > 0) {
    notes.push(
      `NARROWED: ${[...new Set(droppedRequired)].join(", ")} dropped from the base's required-field lists because the ` +
        `field is blank in every record of both files. Measurement may only SUBTRACT from an inherited requirement, ` +
        `never add to one.`
    );
  }

  notes.push(
    `FACT — TITLES: ${measurement.duplicateTitleGroups} group(s) of 2 or more records share an identical Title in the ` +
      `baseline. Title is not a key and must never drive dedupe (rule 5); this figure is the baseline for the ` +
      `duplicate-titles alert.`
  );

  const dateSamples = (base.dateSensitiveFields ?? [])
    .filter((field) => measurement.fields.includes(field))
    .map((field) => `${field} ${JSON.stringify(measurement.baselineFields[field]?.samples ?? [])}`)
    .join(", ");
  notes.push(
    `VALIDATION WITHHELD: no validation block is proposed. Observed formats must first be verified against the engine's ` +
      `parsers with zero false positives, as was done for Bellingham v7.` + (dateSamples ? ` Observed samples — ${dateSamples}.` : "")
  );

  if (urlConstant && identity.sourceUrl === undefined) {
    notes.push(
      `FACT — SOURCE URL: ${JSON.stringify(urlConstant.origin)} is the ORIGIN of the constant ${urlConstant.field}, ` +
        `whose full value is ${JSON.stringify(urlConstant.value)}` +
        (urlConstant.strippedParams.length > 0
          ? ` after removing the volatile query parameter(s) ${urlConstant.strippedParams.join(", ")}`
          : "") +
        `. ASSUMPTION — the origin is the right granularity only when this host serves this source alone. A shared ` +
        `multi-tenant host (Oracle Cloud, SciQuest/Jaggaer) needs the fuller path instead, which is a judgment the ` +
        `measurement cannot make; override sourceUrl if so.`
    );
    if (urlConstant.origin !== urlConstant.value.replace(/\/$/, "")) {
      warnings.push(
        `sourceUrl was taken as the origin ${urlConstant.origin}, discarding the path in ${urlConstant.value}. ` +
          `If this host serves more than one source, use the fuller value instead.`
      );
    }
  }

  // Every key the measurement covers is stated EXPLICITLY, including empty
  // arrays. The merge is replace-per-key, so an omitted key inherits the base
  // value — silence would smuggle in a policy the file does not support. The
  // committed nashville-oracle-cloud delta states `fallbackKeys: []` for
  // exactly this reason.
  const delta: SourceProfileDelta = {
    id: identity.id,
    sourceUrl: sourceUrl ?? "",
    ...(identity.displayName ? { displayName: identity.displayName } : {}),
    ...(identity.agency ? { agency: identity.agency } : {}),
    ...(Object.keys(identityValues).length > 0
      ? {
          detection: {
            identityValues,
            ...(urlConstant ? { urlFields: [urlConstant.field], urlPrefixes: [urlConstant.origin] } : {})
          }
        }
      : {}),
    version: 1,
    collectionPath: measurement.collectionPath,
    primaryKey: primary?.fields ?? [],
    fallbackKeys: fallbacks,
    dedupeKey: dedupeKeyFields,
    hardRequiredFields,
    // Always []. A lock, not a default — see the module header.
    safeBackfillFields: [],
    quality: { requiredFields },
    notes
  };

  return { delta, notes, blocked, warnings };
}
